import React, { useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import PublishIcon from "@material-ui/icons/Publish";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import ValidateRecord from "./ValidateRecord";
import axios, { AxiosError } from "axios";
import { appUrl } from "../../../../../../utils/config";
import ValidateDialog from "../../models/ValidateDialog";
import jwtDecode from "jwt-decode";

const useStyles = makeStyles((theme) => ({
  header: {
    height: "15dvh",
    backgroundColor: "#f0f8ff",
    position: "relative",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
  },
  cancel: {
    position: "absolute",
    top: "10px",
    right: "10px",
    cursor: "pointer",
  },
  uploadBox: {
    width: "100%",
    marginTop: "2em",
    border: "1px solid black",
    backgroundColor: "#f5f5f5",
    padding: "2em 0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
  },

  uploadText: {
    marginLeft: "auto",
  },
  uploadIcon: {
    fontSize: "40px",
    marginLeft: "auto",
    marginRight: "20px",
  },
  file: {
    marginTop: "2em",
    display: "flex",
    flexDirection: "column",
    gap: "1em",
    alignItems: "flex-start",
  },
  link: {
    color: "blue",
    textDecoration: "underline",
  },
  linkBox: {
    cursor: "pointer",
    display: "flex",
    gap: "0.5em",
  },
  bold: {
    fontWeight: 500,
  },
  criteria: {
    maxWidth: "500px",
  },
  uploadBtn: {
    backgroundColor: "#ec6535",
    color: "white",
    textTransform: "none",
    borderRadius: "50px",
    margin: "1em auto",
    padding: "5px 30px",

    "&:hover": {
      backgroundColor: "#ec6535",
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

function getSteps() {
  return ["Upload record(s)", "Validation summary"];
}

export default function UploadCMO({ handleClose, vendorData }) {
  const classes = useStyles();
  const [fileType, setFileType] = useState("xlsx");
  const [selectedFile, setSelectedFile] = useState(null);

  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validation, setValidation] = useState(false);
  const [validationData, setValidationData] = useState([]);
  const steps = getSteps();

  const fileInputRef = useRef();
  const handleCancel = () => {
    handleClose();
  };
  const handleChange = (e) => {
    setFileType(e.target.value);
  };
  const handleUploadBoxClick = () => {
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const getAcceptAttribute = (type) => {
    switch (type) {
      case "xlsx":
        return ".xlsx, .xls";
      case "csv":
        return ".csv";
      case "json":
        return ".json";
      case "xml":
        return ".xml";
      default:
        return "";
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const cancelFile = () => {
    setError(null);
    setSelectedFile(null);
    // Reset the input value:
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the input's value
    }
  };

  const getOrganizationIdFromToken = () => {
    const authToken = localStorage.getItem("authToken"); // Get token from localStorage
    if (!authToken) return null; // Return null if no token is found
  
    try {
      const decodedToken = jwtDecode(authToken); // Decode the JWT token
      return decodedToken.organization_id || null; // Return organization_id or null if not found
    } catch (error) {
      console.error("Error decoding JWT token:", error);
      return null;
    }
  };
  
  // Usage
  const organizationIdFromToken = getOrganizationIdFromToken();
  //console.log("Organization ID:", organizationIdFromToken);


  const handleUpload = () => {
    setLoading(true);
    setError(null);
    setValidation(true);
    const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/uploadCMOInventory`;

    const formData = new FormData();
    formData.append("files", selectedFile);
    formData.append(
      "vendorMosymphonyID",
      // vendorData?.vendorMosymphonyID?.organizationId
      localStorage.collabType === "CMO" ? organizationIdFromToken : vendorData?.vendorMosymphonyID?.organizationId
    );
    formData.append("vendorPlant", vendorData?.vendorPlant?.plantCode);
    formData.append("vendorName",
      localStorage.collabType === "CMO" ? localStorage.organizationName : vendorData?.vendorMosymphonyID?.label);

    axios
      .post(url, formData)
      .then((res) => {
        if (res.data.status) {
          setValidationData(res.data);
          handleNext();
        } else {
          setError(res.data.errorDescription);
        }
      })
      .catch((err) => {
        console.error("Error uploading file: ", err);
        // setError(err.errorDescription || "Error uploading file");
        if (
          err.response &&
          err.response.data &&
          err.response.data.errorDescription
        ) {
          setError(err.response.data.errorDescription); // Access from err.response.data
        } else if (
          err.response &&
          err.response.data &&
          err.response.data.errorMessage
        ) {
          setError(err.response.data.errorMessage); // Access from err.response.data
        } else {
          setError("An error occurred during upload."); // Generic error message
        }
      })
      .finally(() => {
        setLoading(false);
        setValidation(false);
      });
  };
  const displayError = (
    <Container
      style={{
        width: "100%",
        border: "2px solid rgb(255,0,0)",
        backgroundColor: "rgb(255,240,240)",
        opacity: 1,
        textAlign: "center",
        padding: "0.8rem",
        fontSize: "0.875rem",
        marginBottom: "1.5em",
      }}
    >
      <Typography variant="body2">{error}</Typography>
    </Container>
  );
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Container>
            {error && displayError}
            <Box
              className={classes.uploadBox}
              onClick={handleUploadBoxClick}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Typography className={classes.uploadText}>
                Drag & drop file or click here to upload
              </Typography>
              <PublishIcon className={classes.uploadIcon} />
            </Box>
            <input
              type="file"
              hidden
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept={getAcceptAttribute(fileType)}
            />
            <Box className={classes.file}>
              <Typography>Select file type:</Typography>
              <FormControl variant="outlined" size="small">
                <Select value={fileType} onChange={handleChange}>
                  <MenuItem value="xlsx">.xlsx</MenuItem>
                  <MenuItem value="csv">.csv</MenuItem>
                  <MenuItem value="json">.json</MenuItem>
                  <MenuItem value="xml">.xml</MenuItem>
                </Select>
              </FormControl>

              {selectedFile && (
                <Box className={classes.linkBox}>
                  <Typography className={classes.link}>
                    {selectedFile.name}
                  </Typography>
                  <span onClick={cancelFile} style={{ cursor: "pointer" }}>
                    ❌
                  </span>
                </Box>
              )}
              <Box>
                <Typography className={classes.bold}>
                  Validation criteria
                </Typography>
                <Typography className={classes.criteria}>
                  Only excel files accepted (file extensions .csv or .xls or
                  .xlsx)
                </Typography>
                <Typography>
                  1st row should have labels only (as provided in the sample
                  .csv file)
                </Typography>

                <Typography>Should not contain blank row or columns</Typography>
              </Box>
              <Button
                variant="contained"
                className={classes.uploadBtn}
                endIcon={<ChevronRightIcon />}
                // onClick={() => setValidateRecord(true)}
                onClick={handleUpload}
                disabled={!selectedFile}
              >
                Next
              </Button>
            </Box>
          </Container>
        );
      case 1:
        return (
          <ValidateRecord
            handleBack={handleBack}
            validationData={validationData}
            handleClose={handleClose}
          />
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <Box>
      <Box className={classes.header}>
        <Box className={classes.cancel} onClick={handleCancel}>
          <Typography>Cancel ❌</Typography>
        </Box>
        <Typography variant="h4">Upload CMO inventory</Typography>
        <Typography style={{ marginTop: "1em" }}>
          Add/update CMO inventory through file
        </Typography>
      </Box>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {getStepContent(activeStep)}
      {loading && (
        <Backdrop
          className={classes.backdrop}
          open={loading}
          // onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Dialog open={validation} fullWidth>
        <ValidateDialog title="Validating file…" />
      </Dialog>
    </Box>
  );
}
